import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAddOnsByProduct } from '../services/addons';
import type { AddOn } from '../types/addon.types';

export const useAddOnsQuery = (productId?: string) => {
    return useQuery<AddOn[], Error>({
        queryKey: ['addons', 'byProduct', productId ?? null],
        // Read the productId from the queryKey to avoid stale closures
        queryFn: async ({ queryKey }) => {
            const [, , id] = queryKey as ['addons', 'byProduct', string | null];
            if (!id) return [];
            return fetchAddOnsByProduct(id);
        },
        enabled: Boolean(productId), // no request until product is selected
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        // Keep showing the previous list while fetching a new product's add-ons
        placeholderData: (prev) => prev,
    });
};

export const useAvailableAddOns = (productId?: string) => {
    const {
        data: addons = [],
        isLoading,
        error,
        isFetching,
        isSuccess,
        isPlaceholderData,
    } = useAddOnsQuery(productId);

    const getAddOnById = useCallback(
        (id: string) => addons.find((addon) => addon.id === id),
        [addons]
    );
    const getAddOnName = useCallback(
        (id: string) => addons.find((addon) => addon.id === id)?.name ?? '',
        [addons]
    );
    const getAddOnPrice = useCallback(
        (id: string) => addons.find((addon) => addon.id === id)?.price ?? 0,
        [addons]
    );
    const getAddOnGrossPrice = useCallback(
        (id: string) => addons.find((addon) => addon.id === id)?.gross_price ?? 0,
        [addons]
    );

    return {
        addons,
        isLoading,
        isFetching,
        isSuccess,
        isPlaceholderData,
        error,
        getAddOnById,
        getAddOnName,
        getAddOnPrice,
        getAddOnGrossPrice,
    };
};
